/**
 * V196: ULTRA-PREMIUM Verified Dialog Layout Component
 * 
 * Complete redesign with:
 * - Physics-based confetti with realistic motion (60 particles)
 * - Advanced glassmorphism with depth
 * - Vibrant gradient animations
 * - Premium micro-interactions
 * - Mobile-first responsive design
 * 
 * Shared layout for all Verified-related dialogs:
 * - Paywall: "Get Verified & Boost Your Trust" (3rd-degree unlock)
 * - Thank You: Post-payment confirmation
 * - Status: "You are Verified" info dialog
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { DialogClose } from '@/components/ui/dialog';
import { Shield, Link2, CheckCircle2, Sparkles, X } from 'lucide-react';

export interface VerifiedDialogBullet {
  label: string;
  description?: string;
  icon?: React.ReactNode;
}

export interface VerifiedDialogLayoutProps {
  variant: 'paywall' | 'thankyou' | 'status';
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  accentColor?: 'purple' | 'green';
  bullets?: VerifiedDialogBullet[];
  footerNote?: React.ReactNode;
  primaryCtaLabel: string;
  onPrimaryCta: () => void;
  primaryCtaLoading?: boolean;
  primaryCtaDisabled?: boolean;
  secondaryCtaLabel?: string;
  onSecondaryCta?: () => void;
  showCloseButton?: boolean;
  onClose?: () => void;
  children?: React.ReactNode;
  showConfetti?: boolean;
}

// V196.11: NUCLEAR FIX - Direct CSS keyframes with unique name
const Confetti: React.FC = () => {
  const emojis = ['💜', '✨', '🎉', '⭐', '💎', '🚀', '🎊', '🌟', '💫', '✨', '🔥', '⚡'];

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-[100]">
      {Array.from({ length: 60 }).map((_, i) => {
        const emoji = emojis[Math.floor(Math.random() * emojis.length)];
        const x = Math.random() * 100;
        const delay = Math.random() * 3;
        const duration = 3 + Math.random() * 3;

        return (
          <div
            key={i}
            className="absolute text-3xl sm:text-4xl"
            style={{
              left: `${x}%`,
              top: '-10%',
              willChange: 'transform, opacity',
            }}
          >
            <div
              style={{
                animation: `confettiFall ${duration}s linear ${delay}s forwards`,
              }}
            >
              {emoji}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// V196.4: Refined sparkle icon with elegant glow
const SparkleIcon: React.FC<{ icon: React.ReactNode }> = ({ icon }) => {
  return (
    <div className="relative inline-flex items-center justify-center">
      {/* Subtle orbiting sparkles */}
      <div className="absolute inset-0 animate-spin" style={{ animationDuration: '12s' }}>
        {[0, 90, 180, 270].map((angle) => (
          <Sparkles
            key={angle}
            className="absolute w-2.5 h-2.5 sm:w-3 sm:h-3 text-white/40 animate-pulse"
            style={{
              top: '50%',
              left: '50%',
              transform: `rotate(${angle}deg) translate(45px) rotate(-${angle}deg)`,
              animationDelay: `${angle * 0.01}s`,
            }}
          />
        ))}
      </div>

      {/* Refined multi-layer glow effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-purple-500/40 via-pink-500/40 to-fuchsia-500/40 rounded-full blur-3xl opacity-50 animate-pulse-slow scale-125" />
      <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/30 via-purple-400/30 to-pink-400/30 rounded-full blur-2xl opacity-30 animate-pulse scale-110" />
      
      {/* Icon container with refined gradient */}
      <div className="relative bg-gradient-to-br from-purple-500/95 via-pink-500/95 to-fuchsia-500/95 rounded-full p-4 sm:p-5 shadow-2xl border border-white/20 hover:border-white/30 transition-all duration-300">
        <div className="text-white drop-shadow-lg [&>svg]:w-8 [&>svg]:h-8 sm:[&>svg]:w-10 sm:[&>svg]:h-10">
          {icon}
        </div>
      </div>
    </div>
  );
};

export const VerifiedDialogLayout: React.FC<VerifiedDialogLayoutProps> = ({
  variant,
  title,
  subtitle,
  icon,
  accentColor = 'purple',
  bullets = [],
  footerNote,
  primaryCtaLabel,
  onPrimaryCta,
  primaryCtaLoading = false,
  primaryCtaDisabled = false,
  secondaryCtaLabel,
  onSecondaryCta,
  showCloseButton = true,
  onClose,
  children,
  showConfetti = false,
}) => {
  const { t } = useTranslation();
  // Debug logging
  if (import.meta.env.DEV) {
    console.log('🎨 V196: VerifiedDialogLayout render', {
      variant,
      bulletCount: bullets.length,
      bullets: bullets.map(b => b.label),
    });
  }

  // Default icon based on variant
  const defaultIcon = variant === 'thankyou'
    ? <Link2 className="h-10 w-10 sm:h-12 sm:w-12 text-white" />
    : variant === 'status'
    ? <CheckCircle2 className="h-10 w-10 sm:h-12 sm:w-12 text-white" />
    : <Shield className="h-10 w-10 sm:h-12 sm:w-12 text-white" />;

  const displayIcon = icon || defaultIcon;

  // V281.4: Match ThirdDegreeGate dark slate + purple/blue gradient backgrounds
  const backgroundClass = {
    thankyou: 'bg-gradient-to-b from-slate-950 via-slate-900 to-[#050011]',
    paywall: 'bg-gradient-to-b from-slate-950 via-slate-900 to-[#050011]',
    status: 'bg-gradient-to-b from-slate-950 via-slate-900 to-[#050011]',
  }[variant];

  // V281.4: Match ThirdDegreeGate card styling - dark panels with subtle borders
  const bulletColors = [
    {
      bg: 'bg-slate-900/40',
      border: 'border-white/10',
      iconBg: 'bg-gradient-to-br from-emerald-400 to-teal-600 shadow-xl shadow-emerald-500/30',
      glow: 'hover:border-purple-500/30',
      textGradient: 'from-white to-gray-100',
    },
    {
      bg: 'bg-slate-900/40',
      border: 'border-white/10',
      iconBg: 'bg-gradient-to-br from-blue-400 to-cyan-600 shadow-xl shadow-blue-500/30',
      glow: 'hover:border-purple-500/30',
      textGradient: 'from-white to-gray-100',
    },
    {
      bg: 'bg-slate-900/40',
      border: 'border-white/10',
      iconBg: 'bg-gradient-to-br from-purple-400 to-violet-600 shadow-xl shadow-purple-500/30',
      glow: 'hover:border-purple-500/30',
      textGradient: 'from-white to-gray-100',
    },
    {
      bg: 'bg-slate-900/40',
      border: 'border-white/10',
      iconBg: 'bg-gradient-to-br from-pink-400 to-rose-600 shadow-xl shadow-pink-500/30',
      glow: 'hover:border-purple-500/30',
      textGradient: 'from-white to-gray-100',
    },
  ];

  return (
    <div
      className={`
        relative ${backgroundClass}
        w-full h-full
        flex flex-col
        overflow-hidden
      `}
    >
      {/* Confetti */}
      {showConfetti && <Confetti />}

      {/* V281.4: Subtle purple glow accent (match ThirdDegreeGate) */}
      <div className="absolute inset-0 opacity-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-tr from-purple-600/15 via-blue-600/10 to-transparent" />
      </div>

      {/* V281.4: Subtle border (match ThirdDegreeGate) */}
      <div className="absolute inset-0 border border-white/10 pointer-events-none" />

      {/* Header Section — safe-area-inset-top pushes content below Dynamic Island / notch */}
      <div
        className="relative z-10 px-5 md:px-8 pb-1.5 flex-shrink-0"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            {/* Icon */}
            {displayIcon && (
              <div className="flex justify-center mb-2 sm:mb-3">
                <SparkleIcon icon={displayIcon} />
              </div>
            )}

            {/* V281.4: Title with white text (match ThirdDegreeGate) */}
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-1.5 sm:mb-2 text-white leading-tight tracking-tight px-1" dir="auto">
              {title}
            </h2>

            {/* V281.4: Subtitle with gray-400 text (match ThirdDegreeGate) */}
            {subtitle && (
              <p className="text-center mb-2 sm:mb-3 text-xs sm:text-sm md:text-base text-gray-400 max-w-2xl mx-auto leading-snug px-1 text-start sm:text-center" dir="auto">
                {subtitle}
              </p>
            )}
          </div>

          {/* V281.4: Close button - positioned with safe area inset */}
          {(variant === 'paywall' || (showCloseButton && onClose)) && (
            <DialogClose asChild>
              <button
                type="button"
                className="
                  inline-flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center
                  rounded-full bg-slate-800/60 hover:bg-slate-700/80 border border-white/10
                  text-white/70 hover:text-white
                  focus:outline-none focus:ring-2 focus:ring-purple-500/50
                  transition-all duration-200
                  ms-3 sm:ms-4 shrink-0
                "
                style={{
                  position: 'absolute',
                  top: 'calc(env(safe-area-inset-top, 0px) + 12px)',
                  insetInlineEnd: '16px',
                  zIndex: 20
                }}
                aria-label="Close dialog"
              >
                <X className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="sr-only">Close</span>
              </button>
            </DialogClose>
          )}
        </div>
      </div>

      {/* Content Section - V281.2: Fixed overflow to prevent dialog height expansion */}
      <div className="relative z-10 flex-1 px-5 md:px-8 pb-3 overflow-y-auto min-h-0">
        {/* Benefit cards with ultra-glassmorphism */}
        {bullets && bullets.length > 0 && (
          <div className="space-y-2 sm:space-y-2.5 mb-2.5 sm:mb-3">
            {bullets.map((bullet, index) => {
              const colors = bulletColors[index % bulletColors.length];
              return (
                <div
                  key={`bullet-${index}-${bullet.label}`}
                  className={`group flex items-start gap-2.5 sm:gap-3 p-3 sm:p-4 rounded-xl ${colors.bg} ${colors.border} border hover:border-white/35 active:border-white/45 transition-all duration-500 hover:scale-[1.015] active:scale-[0.985] ${colors.glow} hover:shadow-2xl cursor-default animate-slide-in-up`}
                  style={{
                    animationDelay: `${index * 0.08}s`,
                  }}
                >
                  {bullet.icon && (
                    <div className={`${colors.iconBg} rounded-lg p-2 sm:p-2.5 shrink-0 transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 group-active:scale-95 shadow-xl`}>
                      <div className="text-white w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center">
                        {bullet.icon}
                      </div>
                    </div>
                  )}
                  <div className="flex-1 min-w-0 pt-0 text-start">
                    <div className={`font-bold text-white text-xs sm:text-sm leading-tight mb-0.5 sm:mb-1 bg-gradient-to-r ${colors.textGradient} bg-clip-text text-transparent`} dir="auto">
                      {bullet.label}
                    </div>
                    {bullet.description && (
                      <div className="text-xs text-white/70 leading-snug" dir="auto">
                        {bullet.description}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Custom children */}
        {children}

        {/* Footer note - improved styling */}
        {footerNote && (
          <div className="mb-1.5 sm:mb-2 text-center">
            {footerNote}
          </div>
        )}
      </div>

      {/* Fixed Footer Section — safe-area-inset-bottom keeps CTAs above home indicator */}
      <div
        className="relative z-10 px-5 md:px-8 pt-2 flex-shrink-0"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
      >
        {/* V281.4: Primary CTA - Match ThirdDegreeGate green→emerald gradient */}
        <Button
          onClick={(e) => {
            e.stopPropagation();
            onPrimaryCta();
          }}
          disabled={primaryCtaLoading || primaryCtaDisabled}
          className="w-full relative overflow-hidden group bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 active:from-green-800 active:to-emerald-800 disabled:from-green-600/40 disabled:to-emerald-600/40 text-white font-bold h-10 sm:h-11 text-sm sm:text-base shadow-lg hover:shadow-xl active:shadow-md hover:scale-[1.02] active:scale-[0.98] disabled:scale-100 transition-all duration-200 rounded-xl border border-white/20 disabled:border-white/5"
          size="lg"
        >
          <span className="relative z-10 flex items-center justify-center gap-2">
            {primaryCtaLoading ? (
              <>
                <div className="animate-spin h-4 w-4 border-[2.5px] border-white border-t-transparent rounded-full" />
                <span>{primaryCtaLabel}</span>
              </>
            ) : (
              <>
                <span>{primaryCtaLabel}</span>
                <span className="text-base group-hover:translate-x-1 group-active:translate-x-0 transition-transform duration-200">→</span>
              </>
            )}
          </span>
        </Button>

        {/* V281.4: Secondary CTA - Match ThirdDegreeGate outline style */}
        {secondaryCtaLabel && onSecondaryCta && (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              onSecondaryCta();
            }}
            variant="outline"
            className="w-full mt-2 border-purple-500/50 text-purple-200 hover:bg-purple-900/30 hover:text-white hover:border-purple-500/70 active:bg-purple-900/50 transition-all duration-200 rounded-xl h-9 sm:h-10 text-xs sm:text-sm font-medium"
          >
            {secondaryCtaLabel}
          </Button>
        )}

        {/* CTO: Microcopy — only shown on paywall, only when there's a subscribe CTA */}
        {variant === 'paywall' && !primaryCtaLoading && (
          <p className="text-center text-xs text-gray-500 mt-2 leading-tight" dir="auto">
            {t('verified.legal.autoRenewing')} • {t('verified.legal.noFreeTrial')} • {t('verified.legal.cancelAnytime')}
          </p>
        )}
      </div>
    </div>
  );
};

export default VerifiedDialogLayout;
